import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);
export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const clearSession = useCallback(() => {
    setUser(null);
  }, []);

  useEffect(() => {
    let active = true;

    const handleSessionExpired = () => {
      if (active) {
        clearSession();
      }
    };

    window.addEventListener(
      "edujac:session-expired",
      handleSessionExpired
    );

    api
      .get("/auth/me")
      .then((response) => {
        if (active) {
          setUser(response.data);
        }
      })
      .catch(() => {
        if (active) {
          clearSession();
        }
      });

    return () => {
      active = false;
      window.removeEventListener(
        "edujac:session-expired",
        handleSessionExpired
      );
    };
  }, [clearSession]);

  const login = useCallback(
    async (identifier, password) => {
      const { data } = await api.post(
        "/auth/login",
        {
          identifier,
          password,
        }
      );

      const nextUser =
        data.user?.role === "super_admin"
          ? {
              ...data.user,
              access_context: null,
              active_school_id: null,
            }
          : data.user;

      setUser(nextUser);

      return nextUser;
    },
    []
  );

  const replaceSession = useCallback(
    (nextUser) => {
      // The backend manages authentication using HttpOnly cookies.
      setUser(nextUser);
      return nextUser;
    },
    []
  );

  const switchBranch = useCallback(
    async (schoolId) => {
      const { data } = await api.post(
        "/access/switch-branch",
        {
          school_id: schoolId,
        }
      );

      replaceSession(data.user);
      return data;
    },
    [replaceSession]
  );

  const returnHome = useCallback(async () => {
    const { data } = await api.post(
      "/access/return-home"
    );

    replaceSession(data.user);
    return data;
  }, [replaceSession]);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout", {});
      clearSession();
    } catch (error) {
      // If the session is already invalid, the browser has no
      // authenticated server session to preserve.
      if (error?.response?.status === 401) {
        clearSession();
        return;
      }

      // A network failure does not clear an HttpOnly cookie.
      throw error;
    }
  }, [clearSession]);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        refreshUser,
        replaceSession,
        switchBranch,
        returnHome,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () =>
  useContext(AuthContext);

export const homeFor = (userOrRole) => {
  const user =
    typeof userOrRole === "string"
      ? { role: userOrRole }
      : userOrRole || {};

  if (user.access_context) {
    return "/school";
  }

  if (user.role === "super_admin") {
    return "/admin";
  }

  if (
    user.role === "school_admin" &&
    user.admin_scope === "organization"
  ) {
    return "/organization";
  }

  if (user.role === "school_admin") {
    return "/school";
  }

  if (user.role === "teacher") {
    return "/teacher";
  }

  if (user.role === "parent") {
    return "/parent";
  }

  if (user.role === "student") {
    return "/student";
  }

  return "/";
};